/** GPU curves — fragment-shader port of `lib/adjustments/curves.ts`.
 *
 *  The CPU path applies two LUTs per channel (combined-RGB then
 *  per-channel). On the GPU we pre-compose them into a single
 *  256-entry LUT per channel and pass each as a uniform float
 *  array, leaving the shader with one index per channel. WebGL2
 *  fragment shaders allow dynamic indexing of uniform value arrays;
 *  256-entry floats × 3 channels ≈ 192 vec4s of uniform storage —
 *  well within the 256 vec4 minimum guarantee.
 *
 *  Byte parity: LUT values come from `buildLUT` (the CPU
 *  authoritative source), so the shader operates on the exact same
 *  numbers per index. Index recovery from the sampled texture uses
 *  `int(floor(v * 255 + 0.5))`, which is exact for the integer
 *  bytes stored in the source.
 *
 *  ── Future portability note ───────────────────────────────────
 *  If a user reports curves rendering incorrectly on a specific
 *  mobile GPU, the most likely cause is the driver's handling of
 *  dynamic indexing on uniform float arrays in fragment shaders.
 *  The GLSL ES 3.00 spec allows it but historical drivers have
 *  edge-cases. The portable alternative is a 256×1 RGBA texture
 *  carrying the three composed LUTs (one channel per LUT) and
 *  sampling with NEAREST filtering. Requires extending
 *  `lib/gpu/runtime.ts` to accept additional textures via the
 *  uniforms map — defer that refactor until convolutions in the
 *  next PR force it. */

import { buildLUT, type CurvesValue } from '../../adjustments/curves';
import { applyShader } from '../runtime';

const CURVES_FRAG = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_source;
uniform float u_lutR[256];
uniform float u_lutG[256];
uniform float u_lutB[256];
out vec4 outColor;

int idx(float v01) {
  // Recover the integer byte index from a [0, 1] float. Exact for
  // any byte value that was written to the source texture in RGBA8.
  return clamp(int(floor(v01 * 255.0 + 0.5)), 0, 255);
}

void main() {
  vec4 c = texture(u_source, v_uv);
  int iR = idx(c.r);
  int iG = idx(c.g);
  int iB = idx(c.b);
  outColor = vec4(
    u_lutR[iR] / 255.0,
    u_lutG[iG] / 255.0,
    u_lutB[iB] / 255.0,
    c.a
  );
}
`;

/** Build the composed LUT for one output channel: input byte i →
 *  per-channel LUT[combined RGB LUT[i]]. Matches the JS double-
 *  lookup in `applyCurvesPixels` exactly. */
function composeLuts(combined: Uint8ClampedArray, perChannel: Uint8ClampedArray): number[] {
  const composed = new Array<number>(256);
  for (let i = 0; i < 256; i++) {
    composed[i] = perChannel[combined[i]];
  }
  return composed;
}

export function applyCurvesOnGpu(
  source: ImageData,
  target: HTMLCanvasElement,
  value: CurvesValue,
): boolean {
  const lutRGB = buildLUT(value.rgb);
  const lutR = composeLuts(lutRGB, buildLUT(value.r));
  const lutG = composeLuts(lutRGB, buildLUT(value.g));
  const lutB = composeLuts(lutRGB, buildLUT(value.b));
  return applyShader({
    source,
    target,
    fragShaderSrc: CURVES_FRAG,
    uniforms: {
      u_lutR: { type: 'float[]', value: lutR },
      u_lutG: { type: 'float[]', value: lutG },
      u_lutB: { type: 'float[]', value: lutB },
    },
  });
}
